import React, { useEffect, useMemo, useState } from 'react';
import {
	DoctorSickLeavesQuery,
	DocumentsWhereInput,
	PaginationOrderHow,
	useDoctorSickLeavesQuery,
	DocumentType,
} from '../../../../../graphql/graphql';
import { useFlatDataList } from '../../../../Base/Hooks/useFlatDataList';
import Loading from '../../../../../Components/Loading';
import { CustomSettingKey } from '../../../../Base/Enums/CustomSettingKey.enum';
import { CustomSettingsContextProvider } from '../../../../CustomSettings/Services/CustomSettingsContext';
import ManagerSickLeavesTable from './ManagerSickLeavesTable';
import styles from './ManagerSickLeaves.module.scss';
import { QueryDataItem } from '../../../../../Interfaces/QueryDataItem';
import ManagerSickLeavesFilter, {
	ManagerSickLeavesFilterValues,
} from './ManagerSickLeavesFilter';
import { setActiveTreatmentTabForPatient } from '../../../../Treatment/Services/treatment.service';

interface Props {
	workplaceId: string;
}

const ManagerSickLeaves = ({ workplaceId }: Props) => {
	const [filters, setFilters] = useState<ManagerSickLeavesFilterValues>({});

	const handleOpenDetail = (
		item: QueryDataItem<DoctorSickLeavesQuery['documents']>,
	) => {
		const patientId = item.user?.id;
		if (!patientId) {
			return;
		}

		setActiveTreatmentTabForPatient('cure', patientId, workplaceId);
		window.location.href = `/profil-pacienta/${patientId}?handle=treatment&treatmentStep=CSSZSickLeaveCertificate&sickLeaveId=${item.id}`;
	};

	const where = useMemo(() => {
		const base: DocumentsWhereInput = {};

		if (filters.doctor?.value) {
			base.createdById = filters.doctor.value;
		}

		if (filters.patientId?.value) {
			base.userId = filters.patientId.value;
		}

		if (filters.state?.value) {
			base.documentItemExtraStatus = filters.state.value;
		}

		return base;
	}, [filters.doctor?.value, filters.patientId?.value, filters.state?.value]);

	const { flatData, appendData, pagination, getNextPage } = useFlatDataList<
		QueryDataItem<DoctorSickLeavesQuery['documents']>
	>({ workplaceId, ...where });

	const { data, isLoading, refetch, isFetching } = useDoctorSickLeavesQuery({
		where: {
			...where,
			workplaceId,
			types: [DocumentType.SickLeave],
		},
		pagination: {
			...pagination,
			order: {
				key: 'created_at',
				how: PaginationOrderHow.Desc,
			},
		},
	});

	useEffect(() => {
		appendData(data?.documents?.data || []);
	}, [data]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<CustomSettingsContextProvider
			customSettingsKey={CustomSettingKey.MANAGER_WORKPLACE_SICK_LEAVES}>
			<div className={styles.wrapper}>
				<ManagerSickLeavesFilter onChange={setFilters} />
				<div className={styles.tableWrapper}>
					<ManagerSickLeavesTable
						data={flatData}
						isLoading={isLoading || isFetching}
						onLoadMore={getNextPage}
						onRefresh={refetch}
						onOpenDetail={handleOpenDetail}
						hasMoreData={Boolean(data?.documents?.meta?.hasNextPage)}
						totalCount={data?.documents?.meta?.count ?? flatData.length}
					/>
				</div>
			</div>
		</CustomSettingsContextProvider>
	);
};

export default ManagerSickLeaves;
